import { Button, ClickAwayListener, Typography } from '@material-ui/core';
import React, { useMemo, useState } from 'react';
import { AlertDialog, FormTextField } from '../../../../components';
import EditIcon from '@material-ui/icons/Edit';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { deleteApiGroup, editApiGroup, fetchApiGroupNameUnique } from '../../../../actions/apiGroups';
import { getApiGroupNameUnique } from '../../../../reducers/apiGroups';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';
import { API_STATUS_DEPRECATED, API_STATUS_ENABLED } from '../../../../constants';

const Status = ({ value, intl, classes }) => {
  let className = null;
  let text = null;
  switch (value) {
    case API_STATUS_DEPRECATED: {
      className = `${classes.status} ${classes.warning}`;
      text = intl.getI18nMessage("label.apis.states.deprecated");
      break;
    }
    case API_STATUS_ENABLED: {
      className = `${classes.status} ${classes.success}`;
      text = intl.getI18nMessage("label.apis.states.enabled");
      break;
    }
  }

  if (!text) return null;

  return <span id='add-api-group-status' data-apim-test='add-api-group-status' className={className}>
    {text}
  </span>
}

Status.propTypes = {
  value: PropTypes.string,
  classes: PropTypes.object,
  intl: PropTypes.object,
}

const Header = (props) => {
  const {
    intl,
    classes,
    setApiGroupName,
    apiGroupUuid,
    apiGroupNameFromApi,
    status,
    payload,
    setAlertMessages,
  } = props;

  const dispatch = useDispatch();
  const apiGroupNameUnique = useSelector((state) => getApiGroupNameUnique(state));

  const [editApiGroupName, setEditGroupName] = useState(false);
  const [isApiGroupNameEmpty, setIsApiGroupNameEmpty] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteApiGroupConfirm, setDeleteApiGroupConfirm] = useState(false);

  const handleDeleteApiGroup = () => {
    setAlertMessages({
      message: "",
      variant: "",
      status: false,
    })
    dispatch(deleteApiGroup(apiGroupUuid));
  };

  const handleChange = (value) => {
    setApiGroupName(value);
    setIsEditing(true);
    setIsApiGroupNameEmpty(false);
  };

  const handleBlur = () => {
    setIsEditing(false);
    setIsApiGroupNameEmpty(payload.name === '');
    if ((payload.name !== undefined) && (payload.name !== '') && (apiGroupNameFromApi !== payload.name.trim())) {
      dispatch(fetchApiGroupNameUnique(payload.name));
    }
  };

  const handleApiGroupStatus = () => {
    setAlertMessages({
      message: "",
      variant: "",
      status: false,
    })
    payload.uuid = apiGroupUuid;
    if (status === 'DEPRECATED') {
      payload.status = 'Enabled';
    } else if (status === 'ENABLED') {
      payload.status = 'Deprecated';
    }
    dispatch(editApiGroup(payload, apiGroupUuid));
  }

  const emptyFieldName = <span className={classes.textError}>{intl.getI18nMessage("error.field.mandatory")}</span>;
  const inValidFieldName = <span className={classes.textError}>{intl.getI18nMessage("label.custom.fields.name.unique")}</span>;

  const isApiGroupNameUnique = apiGroupNameUnique === false;

  const fieldNameHelperText = useMemo(() => {
    if (isEditing) {
      return intl.getI18nMessage("label.api.groups.add.name.helper.text");
    }
    if (isApiGroupNameUnique) {
      return inValidFieldName;
    }
    if (isApiGroupNameEmpty) {
      return emptyFieldName;
    }
    return intl.getI18nMessage("label.api.groups.add.name.helper.text");
  }, [isEditing, isApiGroupNameUnique, isApiGroupNameEmpty]);

  const fieldNameError = !isEditing && (isApiGroupNameUnique || isApiGroupNameEmpty);

  const params = useParams();
  const currentTab = params?.tabName;
  const isApisTab = currentTab === 'apis';

  const nameContainerClass = clsx(classes.flexNonBetween, classes.nameContainer);

  return (
    <div className={classes.contentPadding}>
      <Typography variant='h5'>{intl.getI18nMessage("label.application.details.overview.scrollbar.apigroups.title")}</Typography>
      <div className={clsx(classes.flex, classes.headerContainer)}>
        <ClickAwayListener onClickAway={() => setEditGroupName(false)}>
          <div className={classes.apiGroupNameContainer}>
            {editApiGroupName || !apiGroupUuid || fieldNameError || isEditing ? (
              <FormTextField
                id='add-api-group-name-field'
                fieldContainerClass={classes.fieldContainer}
                helperText={fieldNameHelperText}
                placeholder={intl.getI18nMessage("label.api.groups.add.name.placeholder.text")}
                handleChange={handleChange}
                value={payload.name}
                handleBlur={handleBlur}
                error={fieldNameError}
              />
            )
              : (
                <div className={nameContainerClass}>
                  <Typography id='api-group-name-edit' variant='h1' className={classes.apiGroupName}>{payload.name}</Typography>
                  {
                    !isApisTab && <Button
                      id='api-group-name-edit-button'
                      data-apim-test='api-group-name-edit-button'
                      color="primary"
                      variant="outlined"
                      onClick={() => setEditGroupName(true)}
                      className={classes.editIconButton}
                    >
                      <EditIcon />
                    </Button>
                  }
                </div>
              )}
          </div>
        </ClickAwayListener>
        {apiGroupUuid && (
          <Button
            id='api-groups-delete-button'
            data-apim-test='api-groups-delete-button'
            variant='outlined'
            className={classes.deleteButton}
            onClick={() => setDeleteApiGroupConfirm(true)}
          >
            {intl.getI18nMessage("label.delete.button")}
          </Button>
        )}
      </div>
      {
        apiGroupUuid &&
        <div className={classes.flexNonBetween}>
          <Status
            intl={intl}
            classes={classes}
            value={status}
          />
          <Button
            id='add-api-group-status-button'
            data-apim-test='add-api-group-status-button'
            variant='outlined'
            color='primary'
            onClick={handleApiGroupStatus}
            className={classes.statusButton}
          >
            {status === 'ENABLED' ? intl.getI18nMessage("label.api.groups.edit.status.deprecate") : intl.getI18nMessage("label.user.status.enable")}
          </Button>
        </div>
      }
      <AlertDialog
        isOpen={deleteApiGroupConfirm}
        title={intl.getI18nMessage("label.api.groups.delete.api.group.warning.text")}
        description={intl.getI18nMessage("label.api.groups.delete.api.group.warning.description")}
        cancelText={intl.getI18nMessage("label.login.saml.confirm.cancel")}
        submitText={intl.getI18nMessage("label.request.list.actions.ok")}
        onCancel={() => setDeleteApiGroupConfirm(false)}
        onSubmit={handleDeleteApiGroup}
      />
    </div>
  );
};

export default Header;

Header.propTypes = {
  intl: PropTypes.object,
  classes: PropTypes.object,
  setApiGroupName: PropTypes.func,
  apiGroupUuid: PropTypes.string,
  status: PropTypes.string,
  payload: PropTypes.object,
  apiGroupNameFromApi: PropTypes.string,
  setAlertMessages: PropTypes.func,
}

